import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { IMfaApiResponse, TMfaType } from './mfaTypes';

interface ICompleteMfaSetupInput {
  token: string;
  type: TMfaType;
}

const initialState: IApiState<IMfaApiResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const completeMfaSetupAsync = createAsyncThunk(
  'settings/mfa/complete',
  async (
    { token, type }: ICompleteMfaSetupInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const url =
        type === 'authenticator'
          ? '/settings/mfa/complete/authenticator'
          : '/settings/mfa/complete/email-sms';

      const { data } = await api.post<IMfaApiResponse>(
        url,
        { token },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Two-factor authentication enabled',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IMfaApiResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const completeMfaSetupSlice = createSlice({
  name: 'settings/mfa/complete',
  initialState,
  reducers: {
    resetCompleteMfaSetup: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(completeMfaSetupAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(completeMfaSetupAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(completeMfaSetupAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IMfaApiResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetCompleteMfaSetup } = completeMfaSetupSlice.actions;

export default completeMfaSetupSlice.reducer;
